import { useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Button, TextArea } from '@aimsos/ui';

import { buildChoiceDecision } from '../../../utils/hitlDecision.js';
import { HitlMarkdown, OpenTasksLink, SectionLabel } from './HitlGateCardParts.jsx';

/**
 * Body for the option-chips (choice) gate — the structured brief card. Shows
 * one question at a time with Back / Next navigation (Cursor-style stepper);
 * each question takes an option pick, a custom answer, and/or a
 * workspace-research request. Owns the state only this mode uses:
 * `selections` and `stepIndex`. Per-card reset is handled by the host keying
 * the card on the contract id, so a new gate always mounts fresh.
 *
 * @param {object} props
 * @param {{ gate?: string, render?: object }} props.card
 * @param {ReturnType<import('../../../hooks/useHitlGateDecision.js').useHitlGateDecision>} props.decision
 * @param {(() => void)|null} [props.onOpenTasks]
 * @param {string} [props.translationNamespace='chat']
 */
function ChoiceGateBody({ card, decision, onOpenTasks = null, translationNamespace = 'chat' }) {
  const { t } = useTranslation(translationNamespace);
  // Per-question { selected, custom, research } keyed by question id.
  const [selections, setSelections] = useState({});
  const [stepIndex, setStepIndex] = useState(0);

  const { resolving, run, submit, task } = decision;
  const render = card?.render ?? {};
  const questions = Array.isArray(render.questions) ? render.questions : [];

  const setChoice = (id, patch) =>
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const choiceAction = (done) => () =>
    submit({
      contractId: task.contractId,
      decision: buildChoiceDecision({ gate: card.gate, done, questions, selections }),
    });
  const onSubmitAnswers = () =>
    run(choiceAction(false), 'chat.hitl.answerSent', 'chat.hitl.answerSentDetail');
  const onChoiceContinue = () =>
    run(choiceAction(true), 'chat.hitl.approved', 'chat.hitl.approvedDetail');

  // A submit is enabled once any question has an option, custom text, or a
  // research request — mirroring buildChoiceDecision's throw condition.
  const choiceHasInput = Object.values(selections).some(
    (s) => (s?.selected ?? '').trim() || (s?.custom ?? '').trim() || s?.research,
  );

  // Stepper derivation: clamp the index and expose the current question.
  const totalSteps = questions.length;
  const safeStep = Math.min(stepIndex, Math.max(totalSteps - 1, 0));
  const currentQuestion = questions[safeStep];
  const isFirstStep = safeStep <= 0;
  const isLastStep = safeStep >= totalSteps - 1;
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStepIndex((i) => Math.min(totalSteps - 1, i + 1));

  return (
    <>
      {/* Context preamble — shown once on the first step to keep the docked card compact. */}
      {isFirstStep && render.brief ? <HitlMarkdown content={render.brief} /> : null}
      {isFirstStep && render.agent_note ? (
        <div className="opacity-80">
          <HitlMarkdown content={render.agent_note} />
        </div>
      ) : null}
      {isFirstStep && render.workspace_answers ? (
        <div>
          <SectionLabel>{t('chat.hitl.workspaceAnswers')}</SectionLabel>
          <HitlMarkdown content={render.workspace_answers} />
        </div>
      ) : null}
      {isFirstStep && render.client_questions ? (
        <div>
          <SectionLabel>{t('chat.hitl.openQuestions')}</SectionLabel>
          <HitlMarkdown content={render.client_questions} />
        </div>
      ) : null}

      {currentQuestion ? (
        (() => {
          const q = currentQuestion;
          const pick = selections[q.id] || {};
          const options = Array.isArray(q.options) ? q.options : [];
          return (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-content">{q.question}</p>
              {/* Full-width select-option rows (Claude / Cursor style). */}
              <div className="flex flex-col gap-1.5">
                {options.map((opt) => {
                  const selected = pick.selected === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={resolving}
                      aria-pressed={selected}
                      // toggle off if re-clicked; picking an option clears custom text
                      onClick={() =>
                        setChoice(q.id, { selected: selected ? '' : opt, custom: '' })
                      }
                      className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        selected
                          ? 'border-brand-500 bg-brand-50 text-content dark:bg-brand-900/20'
                          : 'border-edge bg-surface text-content hover:border-brand-300 hover:bg-surface-hover'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                          selected
                            ? 'border-brand-500 bg-brand-500 text-white'
                            : 'border-content-muted/50'
                        }`}
                      >
                        {selected ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span className="min-w-0 flex-1 break-words">{opt}</span>
                    </button>
                  );
                })}
                {/* Ask the workspace to research this question instead of answering. */}
                <button
                  type="button"
                  disabled={resolving}
                  aria-pressed={Boolean(pick.research)}
                  onClick={() => setChoice(q.id, { research: !pick.research })}
                  className={`flex w-full items-center gap-2.5 rounded-lg border border-dashed px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    pick.research
                      ? 'border-brand-500 bg-brand-50 text-content dark:bg-brand-900/20'
                      : 'border-edge text-content-secondary hover:border-brand-300 hover:bg-surface-hover'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      pick.research
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-content-muted/50'
                    }`}
                  >
                    {pick.research ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <Search className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 flex-1">{t('chat.hitl.searchWorkspace')}</span>
                </button>
              </div>
              {q.allow_custom !== false ? (
                <TextArea
                  id={`hitl-choice-${q.id}`}
                  value={pick.custom ?? ''}
                  onChange={(event) =>
                    setChoice(q.id, { custom: event.target.value, selected: '' })
                  }
                  placeholder={t('chat.hitl.customPlaceholder')}
                />
              ) : null}
            </div>
          );
        })()
      ) : (
        <p className="text-sm whitespace-pre-wrap">
          {render.summary || t('chat.hitl.summaryFallback')}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {!isFirstStep ? (
            <Button variant="secondary" size="sm" onClick={goBack} disabled={resolving}>
              {t('chat.hitl.back')}
            </Button>
          ) : null}
          {totalSteps > 1 ? (
            <span className="text-xs text-content-secondary">
              {t('chat.hitl.questionProgress', { current: safeStep + 1, total: totalSteps })}
            </span>
          ) : null}
          {!isLastStep ? (
            <Button className="ml-auto" size="sm" onClick={goNext} disabled={resolving}>
              {t('chat.hitl.next')}
            </Button>
          ) : (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={onSubmitAnswers} disabled={resolving || !choiceHasInput}>
                {t('chat.hitl.submitAnswers')}
              </Button>
              <Button variant="secondary" size="sm" onClick={onChoiceContinue} disabled={resolving}>
                {t('chat.hitl.continueBtn')}
              </Button>
            </div>
          )}
        </div>
        <OpenTasksLink onOpenTasks={onOpenTasks} label={t('chat.hitl.viewInTasks')} />
      </div>
    </>
  );
}

export default ChoiceGateBody;
